import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '@/components/logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Menu,
  Search,
  X,
  LifeBuoy,
  Lightbulb,
  Bug,
  MessageSquare,
  ChevronUp,
  Home
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePermission } from '@/hooks/usePermissions';
import { ProfileSwitcher } from '@/components/ProfileSwitcher';
import { NotificationBell } from '@/components/NotificationBell';
import { ChatBadge } from '@/components/ChatBadge';
import { FeedbackDialog } from '@/components/FeedbackDialog';
import { QuickActions } from '@/components/QuickActions';

export const SharedNavbar = ({ onCreateSpace, isGlobalToolsOpen, onGlobalToolsToggle }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState('general');
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  const { company } = useAuth();
  const canCreateSpaces = usePermission('spaces.create');

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  // Handle search click - always open QuickActions for consistency
  const handleSearchClick = useCallback(() => {
    setQuickActionsOpen(true);
  }, []);

  // Handle keyboard shortcut for search
  useEffect(() => {
    const down = e => {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleSearchClick();
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [handleSearchClick]);

  // Mobile top bar component
  const MobileTopBar = () => (
    <div
      className={`bg-background flex h-14 items-center justify-between px-4 ${
        !isMenuOpen ? 'border-border border-b' : ''
      }`}
    >
      <Button
        variant='ghost'
        onClick={toggleMenu}
        className='relative -ml-2 flex h-9 w-9 items-center justify-center [&_svg]:size-5'
      >
        <span
          className={`absolute transition-all duration-300 ${
            isMenuOpen ? 'rotate-90 opacity-0' : 'rotate-0 opacity-100'
          }`}
        >
          <Menu />
        </span>
        <span
          className={`absolute transition-all duration-300 ${
            isMenuOpen ? 'rotate-0 opacity-100' : '-rotate-90 opacity-0'
          }`}
        >
          <X />
        </span>
      </Button>

      <div className='flex-1 flex justify-center'>
        <Link to='/spaces' className='flex items-center'>
          <Logo width={100} height={26} />
        </Link>
      </div>

      <div className='w-9'></div>
    </div>
  );

  // Navigation items component
  const NavItems = ({ isMobile = false }) => {
    const linkClasses = `flex items-center gap-2 font-medium ${
      isMobile ? 'text-base' : 'text-sm'
    } ${
      isMobile
        ? 'text-muted-foreground'
        : 'text-muted-foreground hover:bg-primary/5 hover:text-foreground transition-colors'
    } px-3 py-2 rounded-md`;

    return (
      <>
        {/* TEMPORARILY HIDDEN: Nav links from Spaces to Processes
        <Link to='/spaces' className={linkClasses}>
          <Layers className='h-4 w-4' />
          <span>Spaces</span>
        </Link>
        {isMobile && (
          <>
            <Link to='/chat' className={linkClasses}>
              <MessageCircle className='h-4 w-4 scale-x-[-1]' />
              <span>Chat</span>
            </Link>
          </>
        )}
        {canManageUsers && (
          <Link to='/company-team' className={linkClasses}>
            <Users className='h-4 w-4' />
            <span>Users</span>
          </Link>
        )}
        <Link to='/qualifications' className={linkClasses}>
          <BookOpen className='h-4 w-4' />
          <span>Qualifications</span>
        </Link>
        <Link to='/trainers' className={linkClasses}>
          <GraduationCap className='h-4 w-4' />
          <span>Trainers</span>
        </Link>
        <Link to='/attendance' className={linkClasses}>
          <CalendarCheck className='h-4 w-4' />
          <span>Attendance</span>
        </Link>
        <Link to='/posts' className={linkClasses}>
          <MessageSquare className='h-4 w-4' />
          <span>Posts</span>
        </Link>
        {can('*') && (
          <Link to='/processes' className={linkClasses}>
            <GitBranch className='h-4 w-4' />
            <span>Processes</span>
          </Link>
        )}
        END TEMPORARILY HIDDEN */}
        {isMobile && (
          <button onClick={handleSearchClick} className={linkClasses}>
            <Search className='h-4 w-4' />
            <span>Search</span>
          </button>
        )}
      </>
    );
  };

  return (
    <>
      {/* Desktop Navbar */}
      <nav className='border-border bg-background hidden h-16 border-b shadow-sm lg:block'>
        <div className='relative flex h-full items-center px-6'>
          {/* Left section: Logo and Company Name */}
          <div className='flex items-center gap-3'>
            <Link
              to='/spaces'
              className='group relative flex items-center justify-center w-9 h-9 rounded-lg transition-colors hover:bg-accent'
              title='Go to Spaces'
            >
              <Logo
                width={36}
                height={36}
                iconOnly={true}
                className='transition-all duration-200 group-hover:opacity-0 group-hover:scale-75'
              />
              <Home className='absolute h-5 w-5 text-foreground transition-all duration-200 opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100' />
            </Link>
            <Separator orientation='vertical' className='h-6' />
            {company && (
              <div className='flex items-center gap-2 px-2'>
                <span className='text-sm font-medium'>{company.name}</span>
              </div>
            )}
          </div>

          {/* Center section: Navigation */}
          <div className='absolute left-1/2 transform -translate-x-1/2 flex items-center gap-x-1'>
            <NavItems />
          </div>

          {/* Right section: notifications and user menu */}
          <div className='ml-auto flex items-center gap-2'>
            {/* Global Tools Toggle */}
            <Button
              variant='ghost'
              className={`relative h-9 px-3 transition-colors ${isGlobalToolsOpen ? 'bg-accent' : ''}`}
              title='Menu'
              onClick={onGlobalToolsToggle}
            >
              <span className='relative flex h-5 w-5 items-center justify-center'>
                <Menu className={`absolute h-5 w-5 transition-all duration-200 ${isGlobalToolsOpen ? 'opacity-0 scale-75' : 'opacity-100 scale-100'}`} />
                <ChevronUp className={`absolute h-5 w-5 transition-all duration-200 ${isGlobalToolsOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`} />
              </span>
              <span className='text-sm font-medium ml-2'>Menu</span>
            </Button>

            {/* Search Button */}
            <Button
              variant='ghost'
              size='icon'
              className='relative h-9 w-9'
              onClick={handleSearchClick}
              title='Search (Ctrl+K)'
            >
              <Search className='h-5 w-5' />
            </Button>

            {/* Chat Button with Unread Badge */}
            <ChatBadge />

            <NotificationBell />

            {/* Support Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant='ghost'
                  size='icon'
                  className='relative h-9 w-9'
                  title='Help & Support'
                >
                  <LifeBuoy className='h-5 w-5' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end' className='w-56'>
                <DropdownMenuItem
                  onClick={() => {
                    setFeedbackType('feature');
                    setFeedbackDialogOpen(true);
                  }}
                >
                  <Lightbulb className='h-4 w-4 mr-2' />
                  <span>Feature Request</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    setFeedbackType('bug');
                    setFeedbackDialogOpen(true);
                  }}
                >
                  <Bug className='h-4 w-4 mr-2' />
                  <span>Report Bug</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    setFeedbackType('general');
                    setFeedbackDialogOpen(true);
                  }}
                >
                  <MessageSquare className='h-4 w-4 mr-2' />
                  <span>General Feedback</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Profile Switcher - Combined with settings menu */}
            <ProfileSwitcher />
          </div>
        </div>
      </nav>

      {/* Mobile Navbar */}
      <nav className='lg:hidden'>
        <MobileTopBar />
      </nav>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div className='border-border bg-background border-b lg:hidden'>
          <div className='flex flex-col'>
            <div className='p-4 border-b'>
              <div className='relative'>
                <Search className='text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform' />
                <Input
                  type='search'
                  placeholder='Search projects, tasks, and more...'
                  className='bg-background pl-10'
                />
              </div>
            </div>
            <div className='flex-grow overflow-y-auto p-2'>
              <div className='flex flex-col'>
                <NavItems isMobile={true} />
              </div>
            </div>
            <Separator />
            <div className='p-2'>
              {/* Profile Switcher for Mobile - includes settings menu */}
              <div className='px-2 py-2'>
                <ProfileSwitcher />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Dialog */}
      <FeedbackDialog
        open={feedbackDialogOpen}
        onOpenChange={setFeedbackDialogOpen}
        type={feedbackType}
      />

      {/* Quick Actions Dialog */}
      <QuickActions
        open={quickActionsOpen}
        setOpen={setQuickActionsOpen}
        onCreateSpace={onCreateSpace}
        canCreateSpaces={canCreateSpaces}
      />
    </>
  );
};
